import React from 'react'
import PropTypes from 'prop-types'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { getUserInfo } from '../../redux/selectors'
import { fetchContact, fetchContacts, addContacts } from '../../redux/actions'
import AddUsersComponent from './AddUsersComponent'
import { isEmpty, cloneDeep, uniq } from 'lodash'
import { requesterEmailAddress } from '../../libs/utils'

const DEFAULT_CONTACT = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  company: '',
  department: '',
  jobTitle: '',
  officePhone: '',
  country: '',
  mobilePhone: '',
  comments: '',
  subscription: null
}

class AddUsersContainer extends React.PureComponent {
  constructor (props) {
    super(props)

    this.state = {
      companies: [],
      contactsToAdd: [],
      isBulkAdd: false,
      dialogOpen: false
    }

    this.addMoreUsers = event => {
      const numberOfUsers = event.target.value ? Number(event.target.value) : 1
      return this.setState(prevState => {
        let contactsToAdd = prevState.contactsToAdd.slice()
        if (numberOfUsers > contactsToAdd.length) {
          const numberOfNewToAdd = numberOfUsers - contactsToAdd.length
          for (let i = 0; i < numberOfNewToAdd; i++) {
            contactsToAdd.push({...DEFAULT_CONTACT, company: this.state.companies[0]})
          }
        } else if (numberOfUsers < contactsToAdd.length) {
          contactsToAdd.length = numberOfUsers
        }
        return {contactsToAdd}
      })
    }
    this.removeContactToAdd = contactIndex => () => this.setState(prevState => { prevState.contactsToAdd.splice(contactIndex, 1); return {contactsToAdd: prevState.contactsToAdd} })
    this.resetContactsToAdd = () => this.setState({contactsToAdd: [{...DEFAULT_CONTACT, company: this.state.companies[0]}], dialogOpen: false, isBulkAdd: false})
    this.updateContactValue = (index, field) => event => {
      const newValue = event.target.value
      return this.setState(prevState => {
        const contactsToAdd = prevState.contactsToAdd.slice()
        contactsToAdd[index][field] = newValue
        return {contactsToAdd}
      })
    }
    this.updateContactSubscription = (contactIndex, productGroup, product, region) => event => {
      const checked = event.target.checked

      return this.setState(prevState => {
        const contactsToAdd = prevState.contactsToAdd
        const productGroupIndex = contactsToAdd[contactIndex].subscription.productGroups.findIndex(x => x.name === productGroup.name)
        const productIndex = contactsToAdd[contactIndex].subscription.productGroups[productGroupIndex].products.findIndex(x => x.name === product.name)
        if (region && !isEmpty(region)) {
          const newRegion = contactsToAdd[contactIndex].subscription.productGroups[productGroupIndex].products[productIndex].regions.find(x => x.name === region.name)
          newRegion.accessStatus = checked ? 'access granted' : 'no access'
        } else {
          let regions = contactsToAdd[contactIndex].subscription.productGroups[productGroupIndex].products[productIndex].regions.slice()
          regions = regions.map(region => ({ ...region, accessStatus: checked ? 'access granted' : 'no access' }))
          contactsToAdd[contactIndex].subscription.productGroups[productGroupIndex].products[productIndex].regions = regions
        }
        return {contactsToAdd}
      })
    }
    this.addBulkUsers = contactsToAdd => {
      this.setState({ contactsToAdd, dialogOpen: true, isBulkAdd: true })
    }
    this.closeDialog = event => {
      // clear contactsToAdd if we're cancelling a bulk add
      this.setState(s => ({dialogOpen: false, isBulkAdd: false, contactsToAdd: s.isBulkAdd ? [{...DEFAULT_CONTACT, company: this.state.companies[0]}] : s.contactsToAdd}))
    }
    this.addContacts = event => {
      if (this.state.dialogOpen !== true) { // to prevent calling when dialogOpen === 'confirm'
        return this.setState({ dialogOpen: true })
      }

      const addContactsRequest = {
        emailAddress: requesterEmailAddress(this.props),
        contacts: this.state.contactsToAdd.filter(c => c.emailAddress).map(c => {
          // trim down to only regions purchased (=checkable) and "access granted" (=checked)
          // [ { name, products: [ { name, regions: { name, purchased, accessStatus, operationType } } ] } ]
          const subscription = {
            productGroups: (c.subscription || cloneDeep(this.props.contact.subscription)).productGroups.map(productGroup => ({
              name: productGroup.name,
              products: productGroup.products.map(product => ({
                name: product.name,
                regions: product.regions.filter(region => region.purchased && region.accessStatus === 'access granted').map(region => ({
                  name: region.name,
                  purchased: region.purchased,
                  accessStatus: region.accessStatus,
                  operationType: region.operationType
                }))
              })).filter(product => product.regions.length)
            })).filter(productGroup => productGroup.products.length)
          }
          return {
            country: c.country || '',
            department: c.department || '',
            email: c.emailAddress || '',
            firstName: c.firstName || '',
            lastName: c.lastName || '',
            title: c.jobTitle || '',
            mobilePhone: c.mobilePhone || '',
            phone: c.officePhone || '',
            comments: c.comments || '',
            subscription
          }
        })
      }
      this.props.addContacts(addContactsRequest)
      this.setState({dialogOpen: 'confirm'})
    }
  }

  componentDidMount () {
    document.title = 'User Profile - Add Users'
    const emailAddress = requesterEmailAddress(this.props)
    this.props.fetchContact(emailAddress)
    this.props.fetchContacts(emailAddress)
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    let contacts = isEmpty(prevState.contactsToAdd) ? [{...DEFAULT_CONTACT}] : prevState.contactsToAdd
    if (!isEmpty(nextProps.contact) && !isEmpty(nextProps.contact.subscription)) {
      const subscription = cloneDeep(nextProps.contact.subscription)
      for (let i = 0; i < contacts.length; i++) {
        if (isEmpty(contacts[i].subscription)) {
          contacts[i].subscription = subscription
        }
      }
    }
    let companies = []
    if (!isEmpty(nextProps.contacts)) {
      companies = uniq(nextProps.contacts.map(x => x.company))
      for (const c of contacts.filter(c => isEmpty(c.company))) {
        c.company = companies[0]
      }
    }
    return {
      companies,
      contactsToAdd: contacts
    }
  }

  render () {
    const { contactsToAdd, companies, dialogOpen, isBulkAdd } = this.state
    return (
      <AddUsersComponent
        contactsToAdd={contactsToAdd}
        companies={companies}
        addMoreUsers={this.addMoreUsers}
        updateContactValue={this.updateContactValue}
        updateContactSubscription={this.updateContactSubscription}
        resetContactsToAdd={this.resetContactsToAdd}
        removeContactToAdd={this.removeContactToAdd}
        addContacts={this.addContacts}
        addBulkUsers={this.addBulkUsers}
        closeDialog={this.closeDialog}
        dialogOpen={dialogOpen}
        isBulkAdd={isBulkAdd}
        emailAddress={requesterEmailAddress(this.props)}
      />
    )
  }
}

AddUsersContainer.propTypes = {
  emailAddress: PropTypes.string.isRequired,
  contact: PropTypes.object,
  contacts: PropTypes.array
}

const mapStateToProps = state => ({
  emailAddress: getUserInfo(state).emailAddress,
  contact: getUserInfo(state).contact,
  contacts: getUserInfo(state).contacts
})

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    addContacts,
    fetchContact,
    fetchContacts
  }, dispatch)
)

const AddUsersConnect = connect(mapStateToProps, mapDispatchToProps)(AddUsersContainer)

export default AddUsersConnect
